<template>
  <div class="modal-overlay">
    <div class="mmodal-content" style="max-width: 80vw">
      <div class="modal-header">
        <h3>{{ editingRecord ? "Edit" : "Add" }} Newborn</h3>
        <button type="button" class="close" @click="closeModal">&times;</button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveRecord">
          <div class="form-grid">
            <div class="form-group">
              <label>Birth Order</label>
              <input type="text" v-model="form.birth_order" placeholder="e.g. 1st, 2nd twin" class="form-control" />
            </div>
            <div class="form-group">
              <label>Sex *</label>
              <select v-model="form.sex" required class="form-control">
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Ambiguous">Ambiguous</option>
              </select>
            </div>
            <div class="form-group">
              <label>Birth Outcome *</label>
              <select v-model="form.birth_outcome" required class="form-control">
                <option value="">Select</option>
                <option value="Live birth">Live birth</option>
                <option value="Fresh stillbirth">Fresh stillbirth</option>
                <option value="Macerated stillbirth">Macerated stillbirth</option>
              </select>
            </div>

            <div class="form-group">
              <label>Birth Weight (g)</label>
              <input type="number" v-model="form.birth_weight_grams" min="0" class="form-control" />
            </div>
            <div class="form-group">
              <label>Length (cm)</label>
              <input type="number" v-model="form.birth_length_cm" min="0" class="form-control" />
            </div>
            <div class="form-group">
              <label>Head Circumference (cm)</label>
              <input type="number" v-model="form.head_circumference_cm" min="0" class="form-control" />
            </div>

            <div class="form-group">
              <label>APGAR 1 min</label>
              <input type="number" v-model="form.apgar_1min" min="0" max="10" class="form-control" />
            </div>
            <div class="form-group">
              <label>APGAR 5 min</label>
              <input type="number" v-model="form.apgar_5min" min="0" max="10" class="form-control" />
            </div>
            <div class="form-group">
              <label>APGAR 10 min</label>
              <input type="number" v-model="form.apgar_10min" min="0" max="10" class="form-control" />
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.resuscitation_required" />
                Resuscitation required
              </label>
            </div>
            <div class="form-group full-width" v-if="form.resuscitation_required">
              <label>Resuscitation Details</label>
              <textarea v-model="form.resuscitation_details" class="form-control" rows="2"></textarea>
            </div>

            <div class="form-group full-width">
              <label>Congenital Abnormalities</label>
              <textarea v-model="form.congenital_abnormalities" class="form-control" rows="2"></textarea>
            </div>

            <div class="form-group">
              <label class="checkbox-label"><input type="checkbox" v-model="form.vitamin_k_given" /> Vitamin K given</label>
            </div>
            <div class="form-group">
              <label class="checkbox-label"><input type="checkbox" v-model="form.eye_care_given" /> Eye care given</label>
            </div>
            <div class="form-group">
              <label class="checkbox-label"><input type="checkbox" v-model="form.cord_care_given" /> Cord care given</label>
            </div>
            <div class="form-group">
              <label class="checkbox-label"><input type="checkbox" v-model="form.bcg_given" /> BCG given</label>
            </div>
            <div class="form-group">
              <label class="checkbox-label"><input type="checkbox" v-model="form.opv0_given" /> OPV0 given</label>
            </div>
            <div class="form-group">
              <label class="checkbox-label"><input type="checkbox" v-model="form.breastfeeding_initiated" /> Breastfeeding initiated</label>
            </div>

            <div class="form-group full-width">
              <label>Feeding Notes</label>
              <textarea v-model="form.feeding_notes" class="form-control" rows="2"></textarea>
            </div>

            <div class="form-group full-width">
              <label>Notes</label>
              <textarea v-model="form.notes" class="form-control" rows="2"></textarea>
            </div>

            <div class="form-group">
              <label>Recorded By</label>
              <input type="text" v-model="form.recorded_by" class="form-control" />
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? "Saving..." : (editingRecord ? "Update" : "Save") + " Newborn" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { generalApiRequest } from "../../../../../api/apiIndex";

export default {
  name: "NewbornModal",
  props: {
    visitId: { type: [Number, String], default: null },
    editRecord: { type: Object, default: null },
  },
  data() {
    return {
      saving: false,
      editingRecord: null,
      form: this.emptyForm(),
    };
  },
  created() {
    if (this.editRecord) {
      this.editingRecord = this.editRecord;
      this.form = { ...this.emptyForm(), ...this.editRecord };
    }
  },
  methods: {
    emptyForm() {
      return {
        birth_order: "",
        sex: "",
        birth_outcome: "Live birth",
        birth_weight_grams: "",
        birth_length_cm: "",
        head_circumference_cm: "",
        apgar_1min: "",
        apgar_5min: "",
        apgar_10min: "",
        resuscitation_required: false,
        resuscitation_details: "",
        congenital_abnormalities: "",
        vitamin_k_given: false,
        eye_care_given: false,
        cord_care_given: false,
        bcg_given: false,
        opv0_given: false,
        breastfeeding_initiated: false,
        feeding_notes: "",
        notes: "",
        recorded_by: "",
      };
    },
    closeModal() {
      this.$emit("close");
    },
    async saveRecord() {
      this.saving = true;
      try {
        const payload = { ...this.form, visit_id: this.visitId };
        let response;
        if (this.editingRecord) {
          response = await generalApiRequest(`newborns/${this.editingRecord.id}`, "PUT", payload, 1);
        } else {
          response = await generalApiRequest("newborns", "POST", payload, 1);
        }
        if (response.success) {
          this.$emit("saved", response.data);
          this.closeModal();
        } else {
          alert(response.message || "Failed to save newborn record");
        }
      } catch (error) {
        console.error("Error saving newborn record:", error);
        alert("An error occurred while saving the newborn record");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.mmodal-content { background: white; border-radius: 10px; max-height: 90vh; overflow-y: auto; display: flex; flex-direction: column; }
.modal-header { padding: 20px; background: linear-gradient(135deg, #f6ad55 0%, #dd6b20 100%); color: white; display: flex; justify-content: space-between; align-items: center; }
.modal-header h3 { margin: 0; font-size: 20px; }
.close { background: rgb(233, 22, 22); border: 1px solid rgb(182, 178, 178); font-size: 1.5rem; color: white; line-height: 1; cursor: pointer; }
.modal-body { padding: 30px; overflow-y: auto; flex-grow: 1; }
.form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 20px; }
.form-group { margin-bottom: 15px; }
.form-group.full-width { grid-column: 1 / -1; }
.form-group label { display: block; margin-bottom: 5px; font-weight: 600; color: #495057; }
.checkbox-label { display: flex !important; align-items: center; gap: 8px; margin-top: 28px; }
.form-control { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px; font-size: 14px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 15px; }
.btn { padding: 10px 25px; border: none; border-radius: 5px; cursor: pointer; font-weight: 600; }
.btn-secondary { background: #6c757d; color: white; }
.btn-secondary:hover { background: #5a6268; }
.btn-primary { background: #dd6b20; color: white; }
.btn-primary:hover { background: #c05621; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
@media (max-width: 1200px) { .form-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px) { .form-grid { grid-template-columns: 1fr; } }
</style>
